<template>
  <v-container fluid tag="section">
    <base-material-card
      color="indigo"
      icon="mdi-account-edit"
      inline
      :class="['elevation-4', 'user-form-card', $vuetify.breakpoint.xs ? 'px-3 py-3' : 'px-5 py-4']"
    >
      <template v-slot:after-heading>
        <div :class="$vuetify.breakpoint.xs ? 'text-h4' : 'display-2'" class="font-weight-light">
          Edit User
        </div>
      </template>

      <div v-if="isLoading" class="edit-loading">
        <v-progress-circular indeterminate color="indigo" size="42" width="4" />
        <div class="text-caption grey--text mt-3">Loading user…</div>
      </div>

      <formulate-form v-else @submit="update" name="updateForm" :form-errors="formErrors" :errors="inputErrors">
        <div class="top-right-save">
          <v-btn
            type="submit"
            color="indigo"
            dark
            class="save-user-btn"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Update User
          </v-btn>
        </div>
        <v-row class="form-row">
          <!-- User Information Card -->
          <v-col cols="12" md="6" lg="4">
            <v-card class="form-section-card" elevation="2">
              <v-card-title class="section-title">
                <v-icon left color="indigo">mdi-account</v-icon>
                User Information
              </v-card-title>
              <v-card-text class="pt-3">
                <formulate-input
                  v-model="userData.email"
                  type="email"
                  name="email"
                  label="Email"
                  placeholder="Enter email address"
                  validation="required|email"
                  error-behavior="blur"
                />
                <formulate-input
                  v-model="userData.system_domain"
                  type="text"
                  name="system_domain"
                  label="System Domain (to be used in payment url)"
                  placeholder="testdomain"
                  validation="required"
                  error-behavior="blur"
                />
                <formulate-input
                  id="services"
                  v-model="selectedServices"
                  :options="services"
                  type="checkbox"
                  label="Select Services"
                  for="services"
                />
                <formulate-input
                  v-model="userData.allowManual"
                  :options="{ true: 'Yes', false: 'No' }"
                  type="radio"
                  for="allowManual"
                  label="Allow Manual Booking?"
                />
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Provider & API Configuration Card -->
          <v-col cols="12" md="6" lg="4">
            <v-card class="form-section-card" elevation="2">
              <v-card-title class="section-title">
                <v-icon left color="indigo">mdi-server-network</v-icon>
                Provider & API
              </v-card-title>
              <v-card-text class="pt-3">
                <formulate-input
                  v-model="selectedProvider"
                  disabled
                  type="radio"
                  label="Select Provider"
                  name="selectedProvider"
                  :options="{ autocab: 'Autocab', infocab: 'Infocab', icabbi: 'iCabbi' }"
                  for="selectedProvider"
                  :outer-class="(context, classes) => ['mb-0'].concat(classes)"
                  :element-class="(context, classes) => ['sms-gateway-element-class', 'provider-select-input', 'provider-radio-group', 'disabled-class'].concat(classes)"
                />

                <!-- Autocab Form -->
                <formulate-form v-if="selectedProvider === 'autocab'" @submit="fetchCompanies">
                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your Autocab API credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.api_username"
                    type="text"
                    name="api_username"
                    label="API Username"
                    placeholder="Enter API username"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.api_password"
                    type="text"
                    name="api_password"
                    label="API Password"
                    placeholder="Enter API password"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.api_host"
                    type="url"
                    name="api_host"
                    label="API Host"
                    placeholder="Enter API Host URL"
                    validation="required"
                    error-behavior="blur"
                  />
                  <v-btn
                    type="submit"
                    color="success"
                    block
                    :disabled="disableFetchButton"
                    :loading="disableFetchButton"
                    class="fetch-btn"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Fetch Companies
                  </v-btn>
                </formulate-form>

                <!-- Infocab Form -->
                <formulate-form v-else-if="selectedProvider === 'infocab'" @submit="fetchInfoCompanies">
                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your Infocab API credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.infocab_host"
                    type="url"
                    name="infocab_host"
                    label="Infocab Host"
                    placeholder="Enter Infocab host URL"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.infocab_token"
                    type="text"
                    name="infocab_token"
                    label="Infocab Token"
                    placeholder="Enter Infocab Token"
                    validation="requiredIfInfocab"
                    error-behavior="blur"
                  />
                  <v-btn
                    type="submit"
                    color="success"
                    block
                    :disabled="disableInfoFetchButton"
                    :loading="disableInfoFetchButton"
                    class="fetch-btn"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Fetch Companies
                  </v-btn>
                </formulate-form>

                <!-- iCabbi Form -->
                <formulate-form v-else-if="selectedProvider === 'icabbi'" @submit="fetchICabbiCompanies">
                  <div class="credentials-alert credentials-alert-green mb-4">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your iCabbi API credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.icabbi_host"
                    type="url"
                    name="icabbi_host"
                    label="iCabbi URL"
                    placeholder="Enter iCabbi URL"
                    validation="required"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.icabbi_token"
                    type="text"
                    name="icabbi_token"
                    label="iCabbi Token"
                    placeholder="Enter iCabbi Token"
                    validation="required"
                    error-behavior="blur"
                  />
                  <v-btn
                    type="submit"
                    color="success"
                    block
                    :disabled="disableICabbiFetchButton"
                    :loading="disableICabbiFetchButton"
                    class="fetch-btn"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Fetch Companies
                  </v-btn>
                </formulate-form>

                <v-divider class="my-4" v-if="showCompaniesDropdown || (selectedCompanies && selectedCompanies.length)" />

                <!-- Companies Selection -->
                <div v-if="showCompaniesDropdown || (selectedCompanies && selectedCompanies.length)">
                  <div class="formulate-input-label companies-field-label">
                    Companies
                  </div>
                  <v-autocomplete
                    v-if="showCompaniesDropdown"
                    v-model="selectedCompanies"
                    :items="companies"
                    item-text="label"
                    item-value="value"
                    color="success"
                    item-color="success"
                    multiple
                    hide-selected
                    dense
                    outlined
                    hide-details
                    class="companies-picker"
                    placeholder="Search and select companies"
                    :menu-props="{ maxHeight: 320 }"
                  >
                    <!-- Don't render selected values inside the input; we show them as chips below instead -->
                    <template v-slot:selection />
                  </v-autocomplete>

                  <!-- Selected companies as chips under the dropdown -->
                  <div v-if="selectedCompanies && selectedCompanies.length" class="selected-companies-chips mt-3">
                    <v-chip
                      v-for="c in selectedCompanies"
                      :key="c"
                      small
                      close
                      outlined
                      color="indigo"
                      @click:close="removeSelectedCompany(c)"
                    >
                      {{ c }}
                    </v-chip>
                  </div>

                  <div v-if="selectedCompanies && selectedCompanies.length" class="judo-compact mt-3">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <div class="text-caption font-weight-medium grey--text text--darken-2">
                        Judo credentials
                      </div>
                      <div class="text-caption grey--text">
                        Click to add/edit
                      </div>
                    </div>

                    <div class="judo-list">
                      <div v-for="sc in selectedCompanies" :key="sc" class="judo-row">
                        <div class="judo-company-name" :title="sc">{{ sc }}</div>
                        <v-btn
                          x-small
                          outlined
                          :color="hasJudo(sc) ? 'success' : 'indigo'"
                          class="judo-action-btn"
                          @click="setJudoAccount(sc)"
                        >
                          <v-icon x-small left>
                            {{ hasJudo(sc) ? 'mdi-check-circle' : 'mdi-credit-card-outline' }}
                          </v-icon>
                          {{ hasJudo(sc) ? 'Edit Judo' : 'Add Judo' }}
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- SMS Gateway Configuration Card -->
          <v-col cols="12" md="6" lg="4">
            <v-card class="form-section-card" elevation="2">
              <v-card-title class="section-title">
                <v-icon left color="indigo">mdi-message-text</v-icon>
                SMS Gateway
              </v-card-title>
              <v-card-text class="pt-3">
                <formulate-input
                  v-model="userData.sms_service"
                  type="radio"
                  label="SMS Method"
                  name="payment"
                  :options="{ twillio: 'Twilio', whatsApp: 'WhatsApp/URL' }"
                  for="sms"
                  :outer-class="(context, classes) => ['mb-0'].concat(classes)"
                  :element-class="(context, classes) => ['sms-gateway-element-class', 'provider-select-input'].concat(classes)"
                />

                <!-- Twilio Configuration -->
                <formulate-form v-show="userData.sms_service == 'twillio'" type="group">
                  <div class="credentials-alert credentials-alert-green mb-3">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your Twilio credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.twillio.sid"
                    type="text"
                    name="twillio_sid"
                    label="Twilio SID"
                    placeholder="Enter Twilio SID"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.twillio.sid == '' && userData.sms_service == 'twillio'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'SID is required.',
                    }"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.twillio.auth"
                    type="text"
                    name="twillio_auth"
                    label="Twilio Auth Token"
                    placeholder="Enter Auth Token"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.twillio.auth == '' && userData.sms_service == 'twillio'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'Auth token is required.',
                    }"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.twillio.number"
                    type="text"
                    name="twillio_number"
                    label="Twilio Number"
                    placeholder="Enter Twilio Number"
                    validation="twillio_validation"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.twillio.number == '' && userData.sms_service == 'twillio'),
                    }"
                    :validation-messages="{
                      twillio_validation: 'Number is required.',
                    }"
                    error-behavior="blur"
                  />
                </formulate-form>

                <!-- WhatsApp Configuration -->
                <formulate-form v-show="userData.sms_service == 'whatsApp'" type="group">
                  <div class="credentials-alert credentials-alert-green mb-3">
                    <v-icon color="indigo" class="mr-2">mdi-information-outline</v-icon>
                    <span class="credentials-alert-text">Enter your WhatsApp/URL credentials</span>
                  </div>
                  <formulate-input
                    v-model="userData.whatsApp.link"
                    name="link"
                    label="URL Link"
                    placeholder="Enter URL link"
                    validation="twillio_validation"
                    :validation-messages="{
                      twillio_validation: 'URL link is required.',
                    }"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.whatsApp.link == '' && userData.sms_service == 'whatsApp'),
                    }"
                    error-behavior="blur"
                  />
                  <formulate-input
                    v-model="userData.whatsApp.password"
                    type="text"
                    name="password"
                    label="URL Password"
                    placeholder="Enter URL password"
                    validation="twillio_validation"
                    :validation-messages="{
                      twillio_validation: 'URL link password is required.',
                    }"
                    :validation-rules="{
                      twillio_validation: ({ value }) =>
                        !(userData.whatsApp.password == '' && userData.sms_service == 'whatsApp'),
                    }"
                    error-behavior="blur"
                  />
                </formulate-form>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

      </formulate-form>
    </base-material-card>
    <v-dialog
      v-model="dialog"
      :max-width="$vuetify.breakpoint.xs ? '95%' : '620'"
      persistent
    >
      <v-card class="elevation-12 judo-dialog">
        <v-card-title class="success white--text py-3 d-flex align-center">
          <div class="d-flex flex-column">
            <div class="text-subtitle-1 font-weight-medium">Judo Credentials</div>
            <div class="text-caption white--text text--lighten-3">{{ current_judo.company }}</div>
          </div>
          <v-spacer />
          <v-btn icon dark class="judo-close-btn" @click="dialog = false">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="px-5 py-4">
          <formulate-form @submit="saveJudoCredentials" :keep-model-data="true">
            <v-row dense>
              <v-col cols="12" md="6">
                <formulate-input
                  v-model="current_judo.judo_id"
                  type="text"
                  name="judo_id"
                  label="Judo ID"
                  placeholder="Enter Judo ID"
                />
              </v-col>
              <v-col cols="12" md="6">
                <formulate-input
                  v-model="current_judo.judo_api_version"
                  type="text"
                  name="judo_api_version"
                  label="Judo API Version"
                  placeholder="Enter API version"
                />
              </v-col>
              <v-col cols="12" md="6">
                <formulate-input
                  v-model="current_judo.judo_sandbox_url"
                  type="url"
                  name="judo_sandbox_url"
                  label="Judo Sandbox URL"
                  placeholder="Enter Sandbox URL"
                />
              </v-col>
              <v-col cols="12" md="6">
                <formulate-input
                  v-model="current_judo.judo_auth_sandbox"
                  type="text"
                  name="judo_auth_sandbox"
                  label="Judo Auth Sandbox"
                  placeholder="Enter Auth Sandbox"
                />
              </v-col>
            </v-row>

            <v-divider class="my-3" />

            <div class="d-flex align-center justify-end">
              <v-btn text class="mr-2" @click="dialog = false">Cancel</v-btn>
              <v-btn
                v-if="editJudoCredentials"
                color="error"
                outlined
                class="mr-2"
                @click="removeJudoCredentials(current_judo.company)"
              >
                <v-icon left small>mdi-delete</v-icon>
                Remove
              </v-btn>
              <v-btn
                type="submit"
                color="success"
                light
                :disabled="
                  current_judo.judo_id == '' ||
                  current_judo.judo_sandbox_url == '' ||
                  current_judo.judo_auth_sandbox == '' ||
                  current_judo.judo_api_version == ''
                "
              >
                <v-icon left small>mdi-content-save</v-icon>
                Save
              </v-btn>
            </div>
          </formulate-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <base-material-snackbar
      v-model="snackbar"
      type="error"
      v-bind="{
        bottom: true,
        right: true,
      }"
    >
      {{ snackbarText }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
import { _getCompanies, _getInfoCompanies, _getICabbiCompanies, _show, _update, _getServices } from "@/services/User";
// import { defineRule } from '@braid/vue-formulate'

export default {
  name: "EditUser",
  data() {
    return {
    selectedProvider: 'autocab',
      isLoading: true,

      formErrors: [],
      inputErrors: {},
      showCompaniesDropdown: false,
      userData: {
        email: "",
        system_domain: "",
        api_username: "",
        api_password: "",
        api_host: "",
        infocab_host: '',
        infocab_token: '',
        icabbi_host: '',
        icabbi_token: '',
        sms_service: "",
        twillio: {
          sid: "",
          auth: "",
          number: "",
        },
        whatsApp: {
          link: "",
          password: "",
        },
        allowManual: null,
      },
      companies: [],
      selectedCompanies: [],
      services: [],
      selectedServices: [],
      disableFetchButton: false,
    disableInfoFetchButton: false,
    disableICabbiFetchButton: false,

      editedIndex: "",
      apiError: "",
      snackbar: false,
      snackbarText: "",

      dialog: false,

      current_judo: {
        company: "",
        judo_id: "",
        judo_sandbox_url: "",
        judo_api_version: "",
        judo_auth_sandbox: "",
      },
      selectedCompaniesJudo: [],
      editJudoCredentials: false,
      serverSelectedJudoCompanies: [],
      theme: "blue-theme",


    };
 },
  mounted() {
    this.isLoading = true
    this.$formulate.extend({
      rules: {
        requiredIfInfocab: ({ value, getFormValues }) => {
          const provider = getFormValues().selectedProvider
          if (provider === 'infocab' && (!value || value.trim() === '')) {
            return 'Token is required for Infocab'
          }
          return true
        }
      }
    })
    this.getServices();
    this.editedIndex = this.$route.params.id;
    _show(this.editedIndex).then(
      (res) => {
        let localUser = res.data.obj.user;
        this.userData.api_username = localUser.api_username;
        this.userData.api_password = localUser.api_password;
        this.userData.api_host = localUser.api_host;
        this.userData.infocab_host = localUser.infocab_host;
        this.userData.infocab_token = localUser.infocab_token;
        this.userData.icabbi_host = localUser.icabbi_host;
        this.userData.icabbi_token = localUser.icabbi_token;
        this.selectedCompaniesJudo = localUser.judo_accounts.map(
          (j) => j.credentials
        );

        this.serverSelectedJudoCompanies =  JSON.parse(JSON.stringify(this.selectedCompaniesJudo));
        this.userData.email = localUser.email;
        this.userData.system_domain = localUser.system_domain;
        this.userData.allowManual =
          localUser.allow_manual_booking  ? "true" : "false";

        this.selectedServices = localUser.services
          ? localUser.services.map((i) => i.id)
          : [];

          this.selectedProvider = localUser.provider;

        let sel = localUser.companies;
        this.companies = sel.map((v) => ({
          ...v,
          company_id: v.id,
          value: v.name,
          id: "id" + v.id,
          label: v.name,
        }));

        if (sel) {
          this.selectedCompanies = sel.map((v) => v.name);
        }
        this.userData.sms_service = localUser.smsgateway.type;
        if (localUser.smsgateway.type == "twillio") {
          this.userData.twillio = localUser.smsgateway.credentials;
        } else if (localUser.smsgateway.type == "whatsApp") {
          this.userData.whatsApp = localUser.smsgateway.credentials;
        }

        this.isLoading = false;
      },
      (err) => {
         if (err.response && err.response.status) {
          switch (err.response.status) {
            case 422:
              this.inputErrors = err.response.data.errors // assign field errors
              this.formErrors = err.response.data.message
              return
            case 401:

              return
            // ... add lots more cases of bad things that can happen here
          }
        }
        else {
          alert('line 406')
        }

         this.disableFetchButton = false;
         this.isLoading = false;
          this.snackbar = true;
          this.snackbarText = err.message;
        console.log(err.data);
      }
    ).catch((err) => {
          this.disableFetchButton = false;
          this.isLoading = false;
          this.snackbar = true;
          this.snackbarText = err.message;
    });
  },

  watch: {
    selectedProvider(newProvider) {
      // this.companies = [];
      // this.selectedCompanies = [];
      // Emit event or perform logic based on selection
      console.log('Provider switched to:', newProvider)
      this.$emit('provider-changed', newProvider)
    },

    // selectedCompanies(newval) {
    //   this.selectedCompaniesJudo = this.selectedCompaniesJudo.filter((f) =>
    //     newval.includes(f.company)
    //   );
    // },

  },

  methods: {
    removeSelectedCompany (companyName) {
      this.selectedCompanies = (this.selectedCompanies || []).filter((c) => c !== companyName)
      // Keep judo creds in sync if a company is removed.
      this.selectedCompaniesJudo = (this.selectedCompaniesJudo || []).filter((j) => j.company !== companyName)
    },
    hasJudo (companyName) {
      return !!this.selectedCompaniesJudo.find((d) => d.company === companyName)
    },
    getServices() {
      _getServices().then((res) => {
        let objArray1 = res.data.obj;
        this.services = objArray1.map((v) => ({
          ...v,
          value: v.id,
          label: v.name,
        }));
      });
    },

    trimLeadingZeroFromPhone(phone){
      if (phone.startsWith("0")) {
        return phone.slice(1);
      }
      return phone;
    },
    fetchInfoCompanies() {
      this.disableInfoFetchButton = true;
      // this.companies = []
      // this.selectedCompanies = []
      let formData = {
        info_host: this.userData.infocab_host,
        info_token: this.userData.infocab_token,
      }
      _getInfoCompanies(formData)
        .then(
          (response) => {

            this.disableInfoFetchButton = false
            this.apiError = ''
            let data1 = response.data.obj.divisions
            let resCompanies1 = data1.map(function (d) {
              return {
                company_id: d.Id,
                api_company_id: d.Id,
                name: d.Name,
                label: d.Name,
                value: d.Name,
                latitude: (d.latitude !== undefined && d.latitude !== null) ? d.latitude : 0,
                longitude: (d.longitude !== undefined && d.longitude !== null) ? d.longitude : 0,
                info_parent_id : d.CompanyId,
                telephone: d.Phone

              }
            })

          let myArray1 = resCompanies1.filter(
              (el) =>
                !this.companies.some(
                  (data1) => data1.api_company_id == el.company_id
                )
            );

            this.companies = [...this.companies, ...myArray1];
            this.showCompaniesDropdown = this.companies.length > 0

          },
          (error) => {
            this.disableInfoFetchButton = false
            this.showCompaniesDropdown = false
            this.snackbar = true
            this.snackbarText = error.data.responseMessage
          });
    },

    fetchICabbiCompanies () {
      this.disableICabbiFetchButton = true
      let formData = {
        icabbi_host: this.userData.icabbi_host,
        icabbi_token: this.userData.icabbi_token,
      }
      _getICabbiCompanies(formData)
        .then(
          (response) => {
            let data = response.data.obj.body.sites
            this.disableICabbiFetchButton = false

            let resCompanies = data.map(function (d) {
              return {
                company_id: d.id,
                api_company_id: d.id,
                name: d.title,
                label: d.title,
                value: d.title,
                latitude: 0,
                longitude: 0,
                telephone: ''
              };
            });
            let myArray = resCompanies.filter(
              (el) =>
                !this.companies.some(
                  (data) => data.api_company_id == el.company_id
                )
            );
            this.companies = [...this.companies, ...myArray];
            this.showCompaniesDropdown = this.companies.length > 0
          },
          (err) => {
            this.disableICabbiFetchButton = false
            this.showCompaniesDropdown = false
            this.snackbarText = err.data && err.data.responseMessage ? err.data.responseMessage : err.message
            this.snackbar = true
          }
        )
        .catch((err) => {
          this.disableICabbiFetchButton = false
          this.showCompaniesDropdown = false
          this.snackbarText = err.message;
          this.snackbar = true;
        });
    },


    fetchCompanies() {
      this.disableFetchButton = true;
      _getCompanies(this.userData)
        .then(
          (response) => {
            let data = response.data.obj.companies;
            this.disableFetchButton = false;

            let resCompanies = data.map(function (d) {
              return {
                company_id: d.id,
                api_company_id: d.id,
                name: d.name,
                label: d.name,
                value: d.name,
                latitude: d.location.latitude,
                longitude: d.location.longitude,
                telephone: d.telephoneNumber
              };
            });
            let myArray = resCompanies.filter(
              (el) =>
                !this.companies.some(
                  (data) => data.api_company_id == el.company_id
                )
            );
            this.companies = [...this.companies, ...myArray];
            this.showCompaniesDropdown = this.companies.length > 0
          },
          (err) => {
            this.disableFetchButton = false;
            this.showCompaniesDropdown = false
            this.snackbarText = err.data.responseMessage;
            this.snackbar = true;
          }
        )
        .catch((err) => {
          this.disableFetchButton = false;
          this.showCompaniesDropdown = false
          this.snackbarText = err.message;
          this.snackbar = true;
        });
    },
    update() {
      var self = this;
      var itemId = this.editedIndex;
      let objArray = this.companies;
      let sel = this.selectedCompanies;
       this.formErrors= [];
      this.inputErrors= {};
      if(!this.userData.sms_service) {
        alert('Select any sms gateway')
        return;
      }
      var filteredCompanies = [];
      if (objArray) {
        filteredCompanies = objArray.filter((item) => sel.includes(item.name));
      }
      if (filteredCompanies.length) {
        this.userData.companies = filteredCompanies.map((v) => ({
          ...v,
          telephone: v.telephone && v.telephone.startsWith("0") ? v.telephone.slice(1) : (v.telephone || ""),
          judo_account: self.selectedCompaniesJudo.find(
            (item) => item.company === v.name
          ),
        }));
        this.userData.services = this.selectedServices;
        this.userData.allowManual = this.userData.allowManual == "true" ||  this.userData.allowManual == 1 ? 'true' : 'false';
        _update(itemId, this.userData)
          .then(
            (res) => {
              this.$router.push("/users");
            },
            (err) => {
               if (err.data && err.data.status) {
                switch (err.data.status) {
                  case 422:
                    this.inputErrors = err.data.errors // assign field errors
                    this.formErrors = err.data.message
                    return
                  // ... add lots more cases of bad things that can happen here
                }
              }

            }
          )
          .catch((err) => {
            console.log(err.message);
            this.disableFetchButton = false;
            this.snackbar = true;
            this.snackbarText = err.message;
          });
      } else {
        alert("please assigned atleast one company");
      }
    },

    setJudoAccount(company) {

      this.dialog = true;

      var value = this.selectedCompaniesJudo.find(
        (data) => data.company === company
      );
      if (value) {
        this.current_judo = value;
        this.editJudoCredentials = true;
      } else {
        this.editJudoCredentials = false;
        this.current_judo = {
          company: company,
          judo_id: "",
          judo_sandbox_url: "",
          judo_api_version: "",
          judo_auth_sandbox: "",
        };
      }
    },

    saveJudoCredentials() {
      var self = this;
      var value = this.selectedCompaniesJudo.findIndex(
        (data) => data.company === self.current_judo.company
      );
      if (value + 1) {
        self.selectedCompaniesJudo[value] = this.current_judo;
      } else {
        self.selectedCompaniesJudo.push(this.current_judo);
      }
      // if (!this.editJudoCredentials)
      //   this.selectedCompaniesJudo.push(this.current_judo);
      self.dialog = false;
    },

    selectTitleForJudoButton(company) {
      let companyIndex = this.selectedCompaniesJudo.findIndex(
        (item) => item.company === company
      );

      if (companyIndex == -1) return "Add";
      else return "Edit";
    },
    removeJudoCredentials(company) {

      this.selectedCompaniesJudo = this.selectedCompaniesJudo.filter(
        (scj) => scj.company != company
      );
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
.form-row {
  margin: 0;
  align-items: stretch;
  padding-bottom: 35px;
}

.form-section-card {
  height: 100%;
  border-radius: 8px;
  transition: all 0.3s ease;
  margin-bottom: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.form-section-card >>> .v-card__text {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.form-section-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.v-row {
  margin: 0;
}

.v-col {
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
}

.section-title {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-bottom: 2px solid #e0e0e0;
  padding: 16px 20px !important;
  font-weight: 600;
  font-size: 16px;
  color: #424242;
}

.section-title .v-icon {
  margin-right: 8px;
}

.save-button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0;
  border-top: 2px solid #e0e0e0;
  margin-top: 16px;
}

.user-form-card {
  position: relative;
}

.top-right-save {
  position: absolute;
  top: 18px;
  right: 28px;
  z-index: 5;
}

.top-right-save .save-user-btn {
  padding: 10px 18px !important;
  min-width: auto;
}

@media (max-width: 600px) {
  .top-right-save {
    position: static;
    margin: 10px 0 6px;
    display: flex;
    justify-content: flex-end;
  }
}

.save-user-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 16px 48px !important;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(63, 81, 181, 0.25);
  transition: all 0.3s ease;
  background-color: #3f51b5 !important;
  color: #ffffff !important;
}

.save-user-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
}

.fetch-btn {
  border-radius: 6px;
  text-transform: none;
  font-weight: 500;
  margin-top: 8px;
}

/* Companies + Judo compact UX */
.companies-picker {
  margin-top: 6px;
}

.companies-field-label {
  margin-bottom: 8px;
}

.companies-picker >>> .v-chip {
  max-width: 100%;
}

.selected-companies-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.judo-compact {
  border-top: 1px solid #eeeeee;
  padding-top: 12px;
}

.judo-list {
  max-height: 220px;
  overflow-y: auto;
  padding-right: 6px;
}

.judo-list::-webkit-scrollbar {
  width: 6px;
}

.judo-list::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 6px;
}

.judo-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed #eeeeee;
}

.judo-row:last-child {
  border-bottom: none;
}

.judo-company-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.judo-action-btn {
  flex-shrink: 0;
  text-transform: none;
}

.sms-gateway-element-class {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.disabled-class {
  opacity: 0.7;
}

.edit-loading {
  min-height: 55vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.judo-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.judo-dialog >>> .formulate-input {
  margin-bottom: 12px;
}

.judo-close-btn {
  align-self: center;
}
</style>

<style>
/* Form field styling */
.formulate-input {
  margin-bottom: 20px;
}

/* Provider radio: non-selected options light grey, selected option dark */
.provider-radio-group label,
.provider-radio-group .formulate-input-element label {
  color: #bdbdbd !important;
}

/* If markup is: <label><input checked>Text</label> */
.provider-radio-group label:has(input[type="radio"]:checked) {
  color: #424242 !important;
}

/* If markup is: <input checked><label>Text</label> */
.provider-radio-group input[type="radio"]:checked + label {
  color: #424242 !important;
}

/* If markup wraps text in spans next to input */
.provider-radio-group input[type="radio"]:checked + span,
.provider-radio-group input[type="radio"]:checked ~ span {
  color: #424242 !important;
}

.formulate-input-label {
  font-weight: 500;
  color: #424242;
  margin-bottom: 8px;
  font-size: 14px;
}

.formulate-input-element {
  border-radius: 6px;
}

.formulate-input-wrapper {
  margin-bottom: 8px;
}

/* Radio and checkbox styling */
.formulate-input[data-type="radio"] .formulate-input-element,
.formulate-input[data-type="checkbox"] .formulate-input-element {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

/* Group styling */
.formulate-input[data-type="group"] {
  background-color: transparent;
  padding: 0;
  border-radius: 0;
  border: none;
}

/* Vue Formulate "group" sometimes renders its slot content in a horizontal flex row,
   which makes our alert "shrink-to-fit". Force group content to stack vertically. */
.formulate-input[data-type="group"] .formulate-input-element {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

/* Card text styling */
.form-section-card >>> .v-card__text {
  padding: 20px !important;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.form-section-card >>> .v-card__text > *:last-child {
  margin-bottom: 0;
}

/* Credentials Alert styling */
.credentials-alert {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(63, 81, 181, 0.08) 0%, rgba(63, 81, 181, 0.04) 100%);
  border-left: 4px solid #3f51b5;
  border-radius: 6px;
  margin-bottom: 16px;
}

.credentials-alert-text {
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  line-height: 1.4;
}

.credentials-alert .v-icon {
  flex-shrink: 0;
}

.credentials-alert-green {
  background: linear-gradient(135deg, rgba(63, 81, 181, 0.08) 0%, rgba(63, 81, 181, 0.04) 100%) !important;
  border-left-color: #3f51b5 !important;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .formulate-input[data-type="radio"] .formulate-input-element,
  .formulate-input[data-type="checkbox"] .formulate-input-element {
    flex-direction: column;
    gap: 8px;
  }

  .form-section-card {
    margin-bottom: 16px;
  }
}
</style>
